import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SectorRotation from './SectorRotation';
import IntradayScan from './IntradayScan';
import NiftyRealty from './NiftyRealty';
import MomentumRadar from './MomentumRadar';
import Heatmap from './Heatmap';
import OilMonitor from './OilMonitor';
import SWPCalculator from './SWPCalculator';
import SIPCalculator from './SIPCalculator';
import FiiDiiData from './FiiDiiData';
import BulkDealsPage from './BulkDealsPage';
import ProfilePage from './ProfilePage';
import TradersDiary from './TradersDiary';
import './Dashboard.css';

const Dashboard = ({ 
  onLogout, 
  onWatchlistClick, 
  onHighLowClick, 
  onMomentumClick, 
  onRuleStrategyClick, 
  onIndexMoverClick, 
  onNetPositionClick, 
  onDistributionClick,
  onBulkDealsPageClick
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredFeatures, setFilteredFeatures] = useState([]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    const query = searchQuery.toLowerCase().trim();
    
    // Search through trading tools
    const matchedTool = tradingTools.find(tool => 
      tool.title.toLowerCase().includes(query) ||
      tool.subtitle.toLowerCase().includes(query)
    );
    
    if (matchedTool) {
      handleGetStarted(matchedTool.title);
      setSearchQuery(''); // Clear search after successful match
      return;
    }
    
    // Additional search terms mapping
    const searchMappings = {
      'fii': 'FII/DII',
      'dii': 'FII/DII',
      'institutional': 'FII/DII',
      'foreign': 'FII/DII',
      'domestic': 'FII/DII',
      'bulk': 'Bulk Deals/Block Deals',
      'block': 'Bulk Deals/Block Deals',
      'deals': 'Bulk Deals/Block Deals',
      'calculator': 'SIP Calculator',
      'sip': 'SIP Calculator',
      'swp': 'SWP Calculator',
      'momentum': 'Momentum Radar',
      'heatmap': 'Heatmap',
      'heat': 'Heatmap',
      'map': 'Heatmap',
      'sector': 'Sector Rotation',
      'rotation': 'Sector Rotation',
      'intraday': 'Intraday Scan',
      'scan': 'Intraday Scan',
      'swing': 'Swing Zone',
      'zone': 'Swing Zone',
      'oi': 'OI Monitor',
      'monitor': 'OI Monitor',
      'index': 'Index Movers',
      'movers': 'Index Movers',
      'trader': 'Traders Diary',
      'diary': 'Traders Diary',
      'watch': 'Watchlist',
      'watchlist': 'Watchlist',
      'ios': 'iOS App',
      'apple': 'iOS App',
      'android': 'Android App',
      'blog': 'Blogs',
      'blogs': 'Blogs',
      'article': 'Blogs',
      'articles': 'Blogs',
      'learning': 'Advanced Learning',
      'education': 'Advanced Learning',
      'learn': 'Advanced Learning'
    };
    
    const mappedTool = searchMappings[query];
    if (mappedTool) {
      handleGetStarted(mappedTool);
      setSearchQuery(''); // Clear search after successful match
      return;
    }
    
    // If no match found, show alert
    alert(`No feature found for "${searchQuery}". Try searching for: Sector Rotation, Intraday Scan, Momentum Radar, Heatmap, FII/DII, Bulk Deals, etc.`);
  };

  const handleGetStarted = (toolName) => {
    console.log('Getting started with:', toolName);
    switch(toolName) {
      case 'Sector Rotation':
        setCurrentPage('sector-rotation');
        break;
      case 'Intraday Scan':
        setCurrentPage('intraday-scan');
        break;
      case 'Swing Zone':
        setCurrentPage('nifty-realty');
        break;
      case 'OI Monitor':
        setCurrentPage('oil-monitor');
        break;
      case 'Momentum Radar':
        setCurrentPage('momentum-radar');
        break;
      case 'Heatmap':
        setCurrentPage('heatmap');
        break;
      case 'Index Movers':
        if (onIndexMoverClick) onIndexMoverClick();
        break;
      case 'Traders Diary':
        setCurrentPage('traders-diary');
        break;
      case 'SIP Calculator':
        setCurrentPage('sip-calculator');
        break;
      case 'SWP Calculator':
        setCurrentPage('swp-calculator');
        break;
      case 'Watchlist':
        if (onWatchlistClick) onWatchlistClick();
        break;
      case 'FII/DII':
        setCurrentPage('fii-dii');
        break;
      case 'Bulk Deals/Block Deals':
        setCurrentPage('bulk-deals-page');
        break;
      case 'iOS App':
        alert('iOS App download coming soon!');
        break;
      case 'Android App':
        alert('Android App download coming soon!');
        break;
      case 'Blogs':
        navigate('/blog');
        break;
      case 'Advanced Learning':
        alert('Learning materials coming soon!');
        break;
      default:
        setCurrentPage('dashboard');
    }
  };

  const handleBackToDashboard = () => {
    setCurrentPage('dashboard');
  };

  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      const filteredTools = tradingTools.filter(tool =>
        tool.title.toLowerCase().includes(query.toLowerCase()) ||
        tool.subtitle.toLowerCase().includes(query.toLowerCase())
      );
      const filteredAdditional = additionalFeatures.filter(feature =>
        feature.title.toLowerCase().includes(query.toLowerCase()) ||
        feature.subtitle.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredFeatures([...filteredTools, ...filteredAdditional]);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setFilteredFeatures([]);
    }
  };

  const handleFeatureSelect = (feature) => {
    handleGetStarted(feature.title);
    setSearchQuery('');
    setShowDropdown(false);
    setFilteredFeatures([]);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow click on dropdown items
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  // Render different pages based on current page
  if (currentPage === 'sector-rotation') {
    return <SectorRotation onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'intraday-scan') {
    return <IntradayScan onBack={handleBackToDashboard} scanType="intraday" />;
  }
  
  if (currentPage === 'nifty-realty') {
    return <NiftyRealty onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'oil-monitor') {
    return <OilMonitor onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'momentum-radar') {
    return <MomentumRadar onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'heatmap') {
    return <Heatmap onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'swp-calculator') {
    return <SWPCalculator onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'sip-calculator') {
    return <SIPCalculator onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'traders-diary') {
    return <TradersDiary onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'fii-dii') {
    return <FiiDiiData onBack={handleBackToDashboard} />;
  }
  
  if (currentPage === 'bulk-deals-page') {
    return <BulkDealsPage onBack={handleBackToDashboard} />;
  }

  if (currentPage === 'profile') {
    return <ProfilePage onBack={handleBackToDashboard} />;
  }

  // Trading tools data
  const tradingTools = [
    {
      id: 1,
      icon: '📊',
      title: 'Sector Rotation',
      subtitle: 'Identify sector strength',
      color: '#8B5CF6'
    },
    {
      id: 2,
      icon: '📈',
      title: 'Intraday Scan',
      subtitle: 'Identify performing stocks',
      color: '#10B981'
    },
    {
      id: 3,
      icon: '🎯',
      title: 'Swing Zone',
      subtitle: 'Identify the trend',
      color: '#F59E0B'
    },
    {
      id: 5,
      icon: '📡',
      title: 'Momentum Radar',
      subtitle: 'Identify trades, regions & themes performing well',
      color: '#8B5CF6'
    },
    {
      id: 4,
      icon: '📊',
      title: 'OI Monitor',
      subtitle: 'Identify trades, regions & themes performing well',
      color: '#EF4444'
    },
    {
      id: 6,
      icon: '🔥',
      title: 'Heatmap',
      subtitle: 'A clear map of winners and loosers',
      color: '#EF4444'
    },
    {
      id: 7,
      icon: '📊',
      title: 'Index Movers',
      subtitle: 'Track the stocks driving indices',
      color: '#10B981'
    },
    {
      id: 8,
      icon: '📦',
      title: 'Bulk Deals/Block Deals',
      subtitle: 'Find the big orders',
      color: '#8B5CF6'
    },
    {
      id: 9,
      icon: '📖',
      title: 'Traders Diary',
      subtitle: 'Maintain your record book',
      color: '#F59E0B'
    },
    {
      id: 10,
      icon: '📱',
      title: 'SIP Calculator',
      subtitle: 'Calculation',
      color: '#3B82F6'
    },
    {
      id: 11,
      icon: '📱',
      title: 'SWP Calculator',
      subtitle: 'Calculation',
      color: '#3B82F6'
    },
    {
      id: 12,
      icon: '🎯',
      title: 'Watchlist',
      subtitle: 'Track your selected stocks',
      color: '#3B82F6'
    }
  ];

  // Additional searchable features (not shown as tool cards)
  const additionalFeatures = [
    {
      id: 'ios',
      icon: '🍎',
      title: 'iOS App',
      subtitle: 'Access your trading account on iOS',
      color: '#8B5CF6'
    },
    {
      id: 'android',
      icon: '🤖',
      title: 'Android App',
      subtitle: 'Access your trading account on Android',
      color: '#10B981'
    },
    {
      id: 'blogs',
      icon: '📖',
      title: 'Blogs',
      subtitle: 'Read our latest articles and market insights',
      color: '#F59E0B'
    },
    {
      id: 'learning',
      icon: '📚',
      title: 'Advanced Learning',
      subtitle: 'Enhance your trading skills with educational resources',
      color: '#EF4444'
    },
    {
      id: 'fiidii',
      icon: '📊',
      title: 'FII/DII',
      subtitle: 'View Foreign and Domestic Institutional Investors data',
      color: '#8B5CF6'
    }
  ];

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          
          <div className="dashboard-search-container">
            <form className="dashboard-search" onSubmit={handleSearch}>
              <input
                type="text"
                placeholder="Search markets"
                value={searchQuery}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={() => searchQuery.trim() && setShowDropdown(true)}
                className="search-input"
              />
              <button type="submit" className="search-button">
                🔍
              </button>
            </form>
            
            {/* Search Dropdown */}
            {showDropdown && filteredFeatures.length > 0 && (
              <div className="search-dropdown">
                {filteredFeatures.map((feature) => (
                  <div
                    key={feature.id}
                    className="search-dropdown-item"
                    onClick={() => handleFeatureSelect(feature)}
                  >
                    <span className="dropdown-icon" style={{ color: feature.color }}>
                      {feature.icon}
                    </span>
                    <div className="dropdown-content">
                      <div className="dropdown-title">{feature.title}</div>
                      <div className="dropdown-subtitle">{feature.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="dashboard-user">
            <button 
              className="user-button" 
              onClick={() => setCurrentPage("profile")}
            >
              Profile
            </button>
            <button 
              className="user-button sign-out" 
              onClick={onLogout}
            >
              Sign Out
            </button>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="dashboard-nav">
          <div className="dashboard-nav-container">
            <div className="nav-mobile-toggle">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="dashboard-container">
          {/* Welcome Section */}
          <section className="welcome-section">
            <h1 className="welcome-title">Welcome Traders,</h1>
            
            <div className="tutorial-section">
              <div className="tutorial-content">
                <h2 className="tutorial-title">Tutorial Video</h2>
                <p className="tutorial-subtitle">Step by step</p>
                <button className="play-button">
                  <span className="play-icon">▶️</span>
                  <span className="play-text">Click here to play tutorial video</span>
                </button>
              </div>
            </div>
          </section>

          {/* Trading Tools Grid */}
          <section className="tools-section">
            <div className="tools-grid">
              {tradingTools.map((tool) => (
                <div key={tool.id} className="tool-card">
                  <div className="tool-icon" style={{ color: tool.color }}>
                    {tool.icon}
                  </div>
                  <div className="tool-content">
                    <h3 className="tool-title">{tool.title}</h3>
                    <p className="tool-subtitle">{tool.subtitle}</p>
                    <button 
                      className="tool-button"
                      onClick={() => handleGetStarted(tool.title)}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* App Download Section */}
          <div className="apps-grid">
            <div className="app-card ios-app">
              <div className="app-icon">🍎</div>
              <div className="app-content">
                <h3 className="app-title">Add Our iOS App</h3>
                <p className="app-subtitle">
                  Access your trading account and live trading signals on the go with our iOS app
                </p>
                <button className="app-button">📱 Download</button>
              </div>
            </div>

            <div className="app-card android-app">
              <div className="app-icon">🤖</div>
              <div className="app-content">
                <h3 className="app-title">Add Our Android App</h3>
                <p className="app-subtitle">
                  Access your trading account and live trading signals on the go with our Android app
                </p>
                <button className="app-button">📱 Download</button>
              </div>
            </div>
          </div>
          
          {/* Additional App Sections */}
          <div className="apps-grid">
            <div className="app-card ios-app">
              <div className="app-icon">📞</div>
              <div className="app-content">
                <h3 className="app-title">FII/DII</h3>
                <p className="app-subtitle">
                  View Foreign and Domestic Institutional Investors data
                </p>
                <button 
                  className="app-button"
                  onClick={() => handleGetStarted('FII/DII')}
                >
                  📊 View Data
                </button>
              </div>
            </div>

            <div className="app-card android-app">
              <div className="app-icon">📧</div>
              <div className="app-content">
                <h3 className="app-title">Blogs</h3>
                <p className="app-subtitle">
                  Read our latest articles and market insights
                </p>
                <button 
                  className="app-button"
                  onClick={() => navigate('/blog')}
                >
                  📖 Read More
                </button>
              </div>
            </div>
          </div>
          
          <div className="apps-grid">
            <div className="app-card ios-app">
              <div className="app-icon">🌐</div>
              <div className="app-content">
                <h3 className="app-title">Advanced learning materials</h3>
                <p className="app-subtitle">
                  Enhance your trading skills with our educational resources
                </p>
                <button className="app-button">📚 Learn More</button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="dashboard-container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
            </div>
            
            <div className="footer-text">
              <p>Your trusted partner for stock market analysis and trading insights.</p>
            </div>
            
            <div className="footer-social">
              <a href="mailto:support@stocks4u.com" className="social-icon">
                📧
              </a>
              <a href="tel:+911234567890" className="social-icon">
                📞
              </a>
              <a href="https://wa.me/911234567890" className="social-icon" target="_blank" rel="noreferrer">
                📱
              </a>
              <a href="https://stocks4u.com" className="social-icon" target="_blank" rel="noreferrer">
                🌐
              </a>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-copyright">
              <p>Design & Technology Copyright 2025</p>
              <p>All rights reserved by <span className="highlight">stocks4u</span></p>
            </div>
            <div className="footer-badges">
              <span className="badge">Subscribe 🇮🇳 🔒</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;